// src/components/appSettings/Collection/CollectionForm/collectionFormHooks.js
import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  createCollectionThunk,
  updateCollectionThunk,
} from "../../../../store/thunks/appSettingsThunks";

// Default form values
const DEFAULT_COLLECTION_FORM = {
  name: "",
  slug: "",
  description: "",
  shortDescription: "",
  filters: {
    categories: [],
    productTypes: [],
    tags: [],
    priceRange: {
      min: null,
      max: null,
    },
    metalTypes: [],
    purities: [],
  },
  display: {
    priority: 0,
    position: null,
    showOnHome: true,
    showInMenu: true,
    cardStyle: "STANDARD",
    gradientColors: [],
    textColor: "",
    badgeText: "",
    badgeColor: "",
  },
  metadata: {
    occasion: "",
    season: "",
    targetGender: "",
    ageGroup: "",
  },
  targeting: {
    branches: [],
    regions: [],
    customerGroups: [],
  },
  isActive: true,
  isFeatured: false,
  isExclusive: false,
  validFrom: null,
  validUntil: null,
  seo: {
    title: "",
    description: "",
    keywords: [],
  },
};

export const useCollectionForm = (initialData = null, mode = "add") => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Form state
  const [formData, setFormData] = useState(() => {
    if (mode === "edit" && initialData) {
      return parseCollectionData(initialData);
    }
    return DEFAULT_COLLECTION_FORM;
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Image state
  const [imageFiles, setImageFiles] = useState({
    hero: null,
    thumbnail: null,
    banner: null,
    icon: null,
  });

  const [imagePreviews, setImagePreviews] = useState({
    hero: null,
    thumbnail: null,
    banner: null,
    icon: null,
  });

  // Update form when initial data changes
  useEffect(() => {
    if (mode === "edit" && initialData) {
      const parsed = parseCollectionData(initialData);
      setFormData(parsed);

      // Set image previews from existing images
      if (initialData.images) {
        setImagePreviews({
          hero: initialData.images.hero || null,
          thumbnail: initialData.images.thumbnail || null,
          banner: initialData.images.banner || null,
          icon: initialData.images.icon || null,
        });
      }
    }
  }, [initialData, mode]);

  // Auto-generate slug from name
  useEffect(() => {
    if (mode === "add" && formData.name && !formData.slug) {
      const autoSlug = formData.name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "");
      setFormData((prev) => ({ ...prev, slug: autoSlug }));
    }
  }, [formData.name, mode]);

  // Handle form field changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Handle nested fields
    if (name.includes(".")) {
      const parts = name.split(".");
      setFormData((prev) => {
        const newData = { ...prev };
        let current = newData;
        for (let i = 0; i < parts.length - 1; i++) {
          current[parts[i]] = { ...current[parts[i]] };
          current = current[parts[i]];
        }
        current[parts[parts.length - 1]] =
          type === "checkbox" ? checked : value;
        return newData;
      });
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }

    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // Handle array changes
  const handleArrayChange = (field, value) => {
    const parts = field.split(".");
    setFormData((prev) => {
      const newData = { ...prev };
      let current = newData;
      for (let i = 0; i < parts.length - 1; i++) {
        current[parts[i]] = { ...current[parts[i]] };
        current = current[parts[i]];
      }
      current[parts[parts.length - 1]] = value;
      return newData;
    });
  };

  // Handle image upload
  const handleImageUpload = (type, file) => {
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size should not exceed 5MB");
      return;
    }

    setImageFiles((prev) => ({
      ...prev,
      [type]: file,
    }));

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreviews((prev) => ({
        ...prev,
        [type]: reader.result,
      }));
    };
    reader.readAsDataURL(file);
  };

  // Handle image remove
  const handleImageRemove = (type) => {
    setImageFiles((prev) => ({
      ...prev,
      [type]: null,
    }));
    setImagePreviews((prev) => ({
      ...prev,
      [type]: null,
    }));
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    // Basic validations
    if (!formData.name || formData.name.trim().length < 3) {
      newErrors.name = "Collection name is required (min 3 characters)";
    }

    if (!formData.slug || formData.slug.trim().length < 3) {
      newErrors.slug = "Slug is required (min 3 characters)";
    }

    // Slug format validation
    if (formData.slug && !/^[a-z0-9-]+$/.test(formData.slug)) {
      newErrors.slug =
        "Slug can only contain lowercase letters, numbers, and hyphens";
    }

    // Image validations for add mode
    if (mode === "add") {
      if (!imageFiles.thumbnail) {
        newErrors.thumbnailImage = "Thumbnail image is required";
      }
    }

    // Date validations
    if (formData.validFrom && formData.validUntil) {
      if (new Date(formData.validFrom) > new Date(formData.validUntil)) {
        newErrors.validUntil = "End date must be after start date";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async () => {
    setSubmitting(true);

    try {
      const submitData = new FormData();

      // Basic fields
      submitData.append("name", formData.name);
      submitData.append("slug", formData.slug);
      submitData.append("description", formData.description || "");
      submitData.append("shortDescription", formData.shortDescription || "");

      // Filters
      (formData.filters.categories || []).forEach((cat) => {
        submitData.append("filters[categories][]", cat);
      });
      (formData.filters.productTypes || []).forEach((type) => {
        submitData.append("filters[productTypes][]", type);
      });
      (formData.filters.tags || []).forEach((tag) => {
        submitData.append("filters[tags][]", tag);
      });
      if (formData.filters.priceRange?.min) {
        submitData.append(
          "filters[priceRange][min]",
          formData.filters.priceRange.min
        );
      }
      if (formData.filters.priceRange?.max) {
        submitData.append(
          "filters[priceRange][max]",
          formData.filters.priceRange.max
        );
      }
      (formData.filters.metalTypes || []).forEach((metal) => {
        submitData.append("filters[metalTypes][]", metal);
      });
      (formData.filters.purities || []).forEach((purity) => {
        submitData.append("filters[purities][]", purity);
      });

      // Display settings
      submitData.append("display[priority]", formData.display.priority || 0);
      if (formData.display.position) {
        submitData.append("display[position]", formData.display.position);
      }
      submitData.append("display[showOnHome]", formData.display.showOnHome);
      submitData.append("display[showInMenu]", formData.display.showInMenu);
      submitData.append("display[cardStyle]", formData.display.cardStyle);
      if (formData.display.textColor) {
        submitData.append("display[textColor]", formData.display.textColor);
      }
      if (formData.display.badgeText) {
        submitData.append("display[badgeText]", formData.display.badgeText);
      }
      if (formData.display.badgeColor) {
        submitData.append("display[badgeColor]", formData.display.badgeColor);
      }

      // Metadata
      if (formData.metadata.occasion) {
        submitData.append("metadata[occasion]", formData.metadata.occasion);
      }
      if (formData.metadata.season) {
        submitData.append("metadata[season]", formData.metadata.season);
      }
      if (formData.metadata.targetGender) {
        submitData.append(
          "metadata[targetGender]",
          formData.metadata.targetGender
        );
      }
      if (formData.metadata.ageGroup) {
        submitData.append("metadata[ageGroup]", formData.metadata.ageGroup);
      }

      // Targeting
      (formData.targeting.branches || []).forEach((branch) => {
        submitData.append("targeting[branches][]", branch);
      });
      (formData.targeting.regions || []).forEach((region) => {
        submitData.append("targeting[regions][]", region);
      });
      (formData.targeting.customerGroups || []).forEach((group) => {
        submitData.append("targeting[customerGroups][]", group);
      });

      // Status flags
      submitData.append("isActive", formData.isActive);
      submitData.append("isFeatured", formData.isFeatured);
      submitData.append("isExclusive", formData.isExclusive);

      // Validity dates
      if (formData.validFrom) {
        submitData.append("validFrom", formData.validFrom);
      }
      if (formData.validUntil) {
        submitData.append("validUntil", formData.validUntil);
      }

      // SEO
      if (formData.seo.title) {
        submitData.append("seo[title]", formData.seo.title);
      }
      if (formData.seo.description) {
        submitData.append("seo[description]", formData.seo.description);
      }
      (formData.seo.keywords || []).forEach((keyword) => {
        submitData.append("seo[keywords][]", keyword);
      });

      // Images
      if (imageFiles.hero) {
        submitData.append("heroImage", imageFiles.hero);
      }
      if (imageFiles.thumbnail) {
        submitData.append("thumbnailImage", imageFiles.thumbnail);
      }
      if (imageFiles.banner) {
        submitData.append("bannerImage", imageFiles.banner);
      }
      if (imageFiles.icon) {
        submitData.append("iconImage", imageFiles.icon);
      }

      // Submit
      if (mode === "add") {
        await dispatch(createCollectionThunk(submitData)).unwrap();
        toast.success("Collection created successfully");
      } else {
        await dispatch(
          updateCollectionThunk({
            id: initialData._id,
            collectionData: submitData,
          })
        ).unwrap();
        toast.success("Collection updated successfully");
      }

      navigate("/app-settings/collections");
    } catch (error) {
      console.error("Submit error:", error);
      toast.error(error.message || `Failed to ${mode} collection`);
    } finally {
      setSubmitting(false);
    }
  };

  return {
    formData,
    setFormData,
    errors,
    setErrors,
    submitting,
    imageFiles,
    imagePreviews,
    handleChange,
    handleArrayChange,
    handleImageUpload,
    handleImageRemove,
    handleSubmit,
    validateForm,
  };
};

// Helper function to parse collection data for edit mode
function parseCollectionData(collection) {
  return {
    name: collection.name || "",
    slug: collection.slug || "",
    description: collection.description || "",
    shortDescription: collection.shortDescription || "",
    filters: {
      categories: collection.filters?.categories || [],
      productTypes: collection.filters?.productTypes || [],
      tags: collection.filters?.tags || [],
      priceRange: {
        min: collection.filters?.priceRange?.min || null,
        max: collection.filters?.priceRange?.max || null,
      },
      metalTypes: collection.filters?.metalTypes || [],
      purities: collection.filters?.purities || [],
    },
    display: {
      priority: collection.display?.priority || 0,
      position: collection.display?.position || null,
      showOnHome:
        collection.display?.showOnHome !== undefined
          ? collection.display.showOnHome
          : true,
      showInMenu:
        collection.display?.showInMenu !== undefined
          ? collection.display.showInMenu
          : true,
      cardStyle: collection.display?.cardStyle || "STANDARD",
      gradientColors: collection.display?.gradientColors || [],
      textColor: collection.display?.textColor || "",
      badgeText: collection.display?.badgeText || "",
      badgeColor: collection.display?.badgeColor || "",
    },
    metadata: {
      occasion: collection.metadata?.occasion || "",
      season: collection.metadata?.season || "",
      targetGender: collection.metadata?.targetGender || "",
      ageGroup: collection.metadata?.ageGroup || "",
    },
    targeting: {
      branches: collection.targeting?.branches || [],
      regions: collection.targeting?.regions || [],
      customerGroups: collection.targeting?.customerGroups || [],
    },
    isActive: collection.isActive !== undefined ? collection.isActive : true,
    isFeatured: collection.isFeatured || false,
    isExclusive: collection.isExclusive || false,
    validFrom: collection.validFrom
      ? new Date(collection.validFrom).toISOString().split("T")[0]
      : null,
    validUntil: collection.validUntil
      ? new Date(collection.validUntil).toISOString().split("T")[0]
      : null,
    seo: {
      title: collection.seo?.title || "",
      description: collection.seo?.description || "",
      keywords: collection.seo?.keywords || [],
    },
  };
}
